import {
  AdminDataTable,
  AdminTableIdentity,
  type AdminTableFacet,
} from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import { CheckCircleIcon, MinusCircleIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { CopyableCode } from "@/components/CopyableCode";
import type { RiderVehicle } from "../../staff-media/staff-media.hooks";
import type { RosterPerson } from "./types";

type RosterTableContainerProps = {
  data: RosterPerson[];
  loading?: boolean;
  showRole: boolean;
  /** Which rank column to show — riders and therapists carry separate types. */
  showRank?: "rider" | "therapist";
  showCode?: boolean;
  /** Riders tab: the plate on record (0279). */
  showPlate?: boolean;
  /** Signed photo URLs by staff id; absent = initials. */
  photos?: ReadonlyMap<string, string>;
  vehicles?: ReadonlyMap<string, RiderVehicle>;
  actionCell: (person: RosterPerson) => ReactNode;
  /** BL-39 — clicking anywhere on the row opens this person's detail. */
  onOpenPerson: (person: RosterPerson) => void;
};

const statusFacets: AdminTableFacet<RosterPerson>[] = [
  {
    id: "status",
    label: "Status",
    options: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
    getValue: (person) => person.status,
  },
];

export function RosterTableContainer({
  data,
  loading = false,
  showRole,
  showRank,
  showCode = false,
  showPlate = false,
  photos,
  vehicles,
  actionCell,
  onOpenPerson,
}: RosterTableContainerProps) {
  const columns: ColumnDef<RosterPerson, unknown>[] = [
    {
      id: "name",
      accessorFn: (person) => person.name,
      header: "Staff member",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.name}
          subtitle={row.original.email}
          image={photos?.get(row.original.id) ?? null}
          fallback={row.original.name.slice(0, 2).toUpperCase()}
        />
      ),
    },
    {
      id: "hub",
      accessorFn: (person) => person.hubName ?? "",
      header: "Hub",
      cell: ({ row }) => row.original.hubName ?? "—",
    },
  ];
  if (showCode) {
    columns.push({
      id: "code",
      accessorFn: (person) => person.therapist_code ?? "",
      header: "Code",
      cell: ({ row }) =>
        row.original.therapist_code ? (
          <CopyableCode label="Therapist Code" value={row.original.therapist_code} />
        ) : (
          "—"
        ),
    });
  }
  if (showRole) {
    columns.push({
      id: "role",
      accessorFn: (person) => person.role,
      header: "Role",
      cell: ({ row }) => (
        <span className="capitalize">{row.original.role.replaceAll("_", " ")}</span>
      ),
    });
  }
  if (showPlate) {
    columns.push({
      id: "plate",
      accessorFn: (person) => vehicles?.get(person.id)?.plate ?? "",
      header: "Plate",
      cell: ({ row }) => {
        const vehicle = vehicles?.get(row.original.id);
        return vehicle ? (
          <Badge mono shape="soft" variant="outline">
            {vehicle.plate}
          </Badge>
        ) : (
          <span className="text-muted-foreground">No plate</span>
        );
      },
    });
  }
  if (showRank) {
    const rankOf = (person: RosterPerson) =>
      showRank === "therapist" ? person.therapist_rank : person.rider_rank;
    columns.push({
      id: "rank",
      accessorFn: (person) => rankOf(person) ?? "",
      header: "Rank",
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1.5">
          <Badge className="capitalize" shape="soft" variant="muted-soft">
            {rankOf(row.original) ?? "bronze"}
          </Badge>
          {/* 0329 — a hand-set rank is skipped by the monthly system until
              released; the marker keeps that visible at roster scale. */}
          {row.original.rank_is_manual ? (
            <span className="text-xs text-muted-foreground">· manual</span>
          ) : null}
        </span>
      ),
    });
  }
  columns.push(
    {
      id: "status",
      accessorFn: (person) => person.status,
      header: "Status",
      cell: ({ row }) => (
        <Badge
          className="capitalize"
          shape="soft"
          variant={row.original.active ? "success-soft" : "warning-soft"}
        >
          {row.original.active ? (
            <CheckCircleIcon weight="bold" />
          ) : (
            <MinusCircleIcon weight="bold" />
          )}
          {row.original.status}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => actionCell(row.original),
    },
  );

  return (
    <AdminDataTable
      presentation="standalone"
      loading={loading}
      onRowClick={onOpenPerson}
      rowLabel={(person) => `Open ${person.name}`}
      data={data}
      columns={columns}
      getRowId={(person) => person.id}
      searchText={(person) =>
        [person.name, person.email, person.hubName, person.therapist_code, person.role].join(" ")
      }
      searchPlaceholder="Search staff, email, hub, or code…"
      facets={statusFacets}
      initialSorting={[{ id: "name", desc: false }]}
      exportFilename="staff-roster"
      exportColumns={[
        { header: "Name", value: (person) => person.name },
        { header: "Email", value: (person) => person.email },
        { header: "Hub", value: (person) => person.hubName },
        { header: "Role", value: (person) => person.role },
        { header: "Status", value: (person) => person.status },
        ...(showCode
          ? [{ header: "Code", value: (person: RosterPerson) => person.therapist_code }]
          : []),
        ...(showPlate
          ? [
              {
                header: "Plate",
                value: (person: RosterPerson) => vehicles?.get(person.id)?.plate ?? "",
              },
            ]
          : []),
        ...(showRank
          ? [
              {
                header: "Rank",
                value: (person: RosterPerson) =>
                  showRank === "therapist" ? person.therapist_rank : person.rider_rank,
              },
            ]
          : []),
      ]}
    />
  );
}
